/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.originalSlides = null;
    this.isInitialized = false;

    // Prevent duplicate instance creation
    if (initializedSliders.has(element)) return;
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Check if should init based on visibility
    this.checkVisibilityAndInit();

    // Listen for resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
    }, 150);
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    // Content container
    this.contentEl = this.element.querySelector('.marquee-content');
    if (!this.contentEl) return;

    let slides;
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      // Direct children become slides (filter out style, script, and other non-content elements)
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter(el => !excludedTags.includes(el.tagName));
      
      if (slides.length < 1) {
        return;
      }

      this.originalSlides = slides;
    }

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);

    // Get options from data attributes
    const spaceBetween = parseInt(this.element.dataset.spaceBetween) || 120;
    const speed = parseInt(this.element.dataset.speed) || 3000;

    // Build marquee options using block variables
    const marqueeOptions = {
      slidesPerView: 'auto',
      spaceBetween: spaceBetween,
      centeredSlides: true,
      loop: true,
      loopedSlides: 10,
      speed: speed,
      allowTouchMove: false,
      autoplay: {
        delay: 1,
        disableOnInteraction: false,
      },
    };
    // Initialize Swiper
    this.swiper = new Swiper(swiperEl, marqueeOptions);

    // Mark as initialized to update CSS
    this.element.setAttribute('data-swiper-initialized', 'true');

    this.isInitialized = true;
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.element.removeAttribute('data-swiper-initialized');
    this.isInitialized = false;
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  const sliders = document.querySelectorAll('[data-slider-block]');
  sliders.forEach(slider => {
    new SliderBlock(slider);
  });
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSliderBlocks();
    waitForSwiper();
  });
} else {
  initSliderBlocks();
  waitForSwiper();
}

// Re-initialize on back/forward navigation
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSliderBlocks();
  }
});

// Export for potential external use
window.SliderBlock = SliderBlock;
window.initSliderBlocks = initSliderBlocks;

